import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import API from '../api';
import BackButton from './common/BackButton';
import { SEMANTIC } from '../styles/theme';
import { useAppModal } from '../hooks/useAppModal';

const fmt = (n) =>
  Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// ======================================================
// TILE CONTO ALPACA
// ======================================================
function AlpacaAccountTile({ account, loading, error, onGoToSettings, tradingMode }) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div style={tileStyle}>
        <div style={{ color: SEMANTIC.textSecondary, fontSize: 14 }}>{t('manage_money.alpaca_loading')}</div>
      </div>
    );
  }

  if (error === 'invalid_credentials') {
    return (
      <div style={{ ...tileStyle, border: `1px solid ${SEMANTIC.actionHold}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <span style={{ fontSize: 22 }}>🦙</span>
          <span style={{ fontWeight: 700, fontSize: 15 }}>{t('manage_money.alpaca_title')}</span>
          <span style={badgeStyle(SEMANTIC.actionHold, 'rgba(180,120,0,0.12)')}>{t('manage_money.invalid_credentials_badge')}</span>
        </div>
        <p style={{ color: SEMANTIC.textSecondary, fontSize: 13, margin: '0 0 12px', lineHeight: 1.5 }}>
          {t('manage_money.invalid_credentials_desc')}
        </p>
        <button
          onClick={onGoToSettings}
          style={{
            background: 'transparent', border: `1px solid ${SEMANTIC.actionHold}`,
            color: SEMANTIC.actionHold, borderRadius: 8, padding: '7px 16px',
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          <i className="bi bi-gear" style={{ marginRight: 6 }} />
          {t('manage_money.invalid_credentials_btn')}
        </button>
      </div>
    );
  }

  if (error || !account) {
    return (
      <div style={{ ...tileStyle, border: `1px solid ${SEMANTIC.actionSell}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <span style={{ fontSize: 22 }}>🦙</span>
          <span style={{ fontWeight: 700, fontSize: 15 }}>{t('manage_money.alpaca_title')}</span>
          <span style={badgeStyle(SEMANTIC.actionSell, 'rgba(200,0,0,0.1)')}>{t('manage_money.alpaca_unavailable')}</span>
        </div>
        <p style={{ color: SEMANTIC.textSecondary, fontSize: 13, margin: 0 }}>{t('manage_money.alpaca_error')}</p>
      </div>
    );
  }

  const statusColor = account.status === 'ACTIVE' ? SEMANTIC.actionBuy : SEMANTIC.actionHold;

  return (
    <div style={{ ...tileStyle, border: `1px solid ${SEMANTIC.actionBuy}` }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 26 }}>🦙</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{t('manage_money.alpaca_title')}</div>
            <div style={{ fontSize: 11, color: SEMANTIC.textSecondary, letterSpacing: 1 }}>{t('manage_money.alpaca_subtitle')}</div>
          </div>
        </div>
        <span style={badgeStyle(statusColor, 'rgba(0,0,0,0.08)')}>{account.status}</span>
      </div>

      {/* Cash prominente */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, color: SEMANTIC.textSecondary, marginBottom: 4, letterSpacing: 1 }}>{t('manage_money.cash_available')}</div>
        <div style={{ fontSize: 36, fontWeight: 700, color: SEMANTIC.actionBuy, letterSpacing: -1 }}>
          {fmt(account.cash)}
        </div>
      </div>

      {/* Grid metriche secondarie */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
        {[
          { labelKey: 'manage_money.metric_portfolio_value', value: account.portfolio_value, color: SEMANTIC.ctaPrimary },
          tradingMode !== 'paper' && { labelKey: 'manage_money.metric_equity',       value: account.equity,       color: SEMANTIC.textPrimary },
          tradingMode !== 'paper' && { labelKey: 'manage_money.metric_buying_power', value: account.buying_power, color: SEMANTIC.actionHold },
          { labelKey: 'manage_money.metric_currency',        value: account.currency,        color: SEMANTIC.textSecondary, raw: true },
        ].filter(Boolean).map(({ labelKey, value, color, raw }) => (
          <div key={labelKey} style={{
            backgroundColor: SEMANTIC.backgroundApp,
            borderRadius: 8,
            padding: '10px 12px',
            border: `1px solid ${SEMANTIC.backgroundDisabled}`,
          }}>
            <div style={{ fontSize: 11, color: SEMANTIC.textSecondary, marginBottom: 3 }}>{t(labelKey)}</div>
            <div style={{ fontWeight: 700, fontSize: 14, color }}>{raw ? value : fmt(value)}</div>
          </div>
        ))}
      </div>

      {/* Nota istruzione banca */}
      <div style={{
        backgroundColor: SEMANTIC.backgroundApp,
        border: `1px solid ${SEMANTIC.actionHold}`,
        borderRadius: 8,
        padding: '10px 14px',
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
      }}>
        <i className="bi bi-info-circle" style={{ color: SEMANTIC.actionHold, fontSize: 15, flexShrink: 0, marginTop: 1 }} />
        <p style={{ margin: 0, fontSize: 12, color: SEMANTIC.textSecondary, lineHeight: 1.5 }}>{t('manage_money.bank_note')}</p>
      </div>
    </div>
  );
}

// ======================================================
// VISUALIZZAZIONE FLUSSO FONDI
// ======================================================
function MoneyFlowChart({ account, portfolios, tradingMode }) {
  const { t } = useTranslation();
  const isPaper = tradingMode === 'paper';

  // I portafogli sono contenuti nel conto Alpaca: l'allocato è il loro valore totale
  // (liquidità + posizioni) rispetto all'equity del conto (cash + posizioni).
  const totalBalance = portfolios.reduce((s, p) => s + Number(p.total_balance || 0), 0);
  const alpacaEquity = account
    ? Number(account.equity ?? (Number(account.cash || 0) + Number(account.portfolio_value || 0)))
    : null;
  const unallocated = alpacaEquity != null ? alpacaEquity - totalBalance : null;

  const allocPct = alpacaEquity != null && alpacaEquity > 0
    ? (totalBalance / alpacaEquity) * 100
    : null;

  return (
    <div style={tileStyle}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16, color: SEMANTIC.textPrimary }}>
        {t('manage_money.flow_title')}
      </div>

      {/* Diagramma: Banca → Alpaca → Portafogli */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: isPaper ? 16 : 24, overflowX: 'auto', paddingBottom: 4 }}>

        {/* Nodo Banca — oscurato in paper trading */}
        <div style={{
          ...flowNodeStyle(isPaper ? '#333' : SEMANTIC.backgroundDisabled, SEMANTIC.backgroundApp),
          opacity: isPaper ? 0.4 : 1,
          filter: isPaper ? 'grayscale(1)' : 'none',
          position: 'relative',
        }}>
          <i className="bi bi-bank" style={{ fontSize: 22, marginBottom: 4 }} />
          <div style={{ fontSize: 11, fontWeight: 700 }}>{t('manage_money.flow_bank')}</div>
          <div style={{ fontSize: 10, color: SEMANTIC.textSecondary, marginTop: 2 }}>{t('manage_money.flow_bank_label')}</div>
          {isPaper && (
            <div style={{
              position: 'absolute', top: -8, right: -8,
              backgroundColor: '#333', color: '#666',
              fontSize: 9, fontWeight: 700, padding: '2px 6px',
              borderRadius: 10, border: '1px solid #444',
              whiteSpace: 'nowrap',
            }}>
              N/A
            </div>
          )}
        </div>

        <ArrowRight color={isPaper ? '#333' : SEMANTIC.textDisabled} label={isPaper ? '—' : t('manage_money.flow_arrow_days')} dashed={isPaper} />

        <div style={flowNodeStyle(account ? SEMANTIC.actionBuy : SEMANTIC.backgroundDisabled, SEMANTIC.backgroundApp)}>
          <span style={{ fontSize: 22 }}>🦙</span>
          <div style={{ fontSize: 11, fontWeight: 700, marginTop: 2 }}>{t('manage_money.flow_alpaca')}</div>
          <div style={{ fontSize: 12, color: SEMANTIC.actionBuy, fontWeight: 700, marginTop: 3 }}>
            {account ? fmt(account.cash) : '—'}
          </div>
        </div>

        <ArrowRight color={SEMANTIC.ctaPrimary} label={t('manage_money.flow_arrow_alloc')} />

        <div style={flowNodeStyle(SEMANTIC.ctaPrimary, SEMANTIC.backgroundApp)}>
          <i className="bi bi-briefcase" style={{ fontSize: 22, marginBottom: 4 }} />
          <div style={{ fontSize: 11, fontWeight: 700 }}>{t('manage_money.flow_portfolios')}</div>
          <div style={{ fontSize: 12, color: SEMANTIC.ctaPrimary, fontWeight: 700, marginTop: 3 }}>{fmt(totalBalance)}</div>
        </div>
      </div>

      {/* Nota paper trading */}
      {isPaper && (
        <div style={{
          backgroundColor: 'rgba(255,209,102,0.06)',
          border: '1px solid rgba(255,209,102,0.2)',
          borderRadius: 8,
          padding: '9px 13px',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          fontSize: 12,
          color: SEMANTIC.textSecondary,
          lineHeight: 1.5,
        }}>
          <i className="bi bi-info-circle" style={{ color: '#ffd166', flexShrink: 0, marginTop: 1 }} />
          <span>{t('manage_money.flow_paper_note')}</span>
        </div>
      )}

      {/* Barra allocazione */}
      {allocPct !== null && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: SEMANTIC.textSecondary, marginBottom: 6 }}>
            <span>{t('manage_money.flow_allocated_label')}</span>
            <span style={{ color: SEMANTIC.textPrimary }}>{allocPct.toFixed(1)}%</span>
          </div>
          <div style={{ backgroundColor: SEMANTIC.backgroundApp, borderRadius: 6, height: 10, overflow: 'hidden', marginBottom: 8, border: `1px solid ${SEMANTIC.backgroundDisabled}` }}>
            <div style={{
              height: '100%',
              width: `${Math.min(100, allocPct)}%`,
              background: `linear-gradient(90deg, ${SEMANTIC.ctaPrimary}, ${SEMANTIC.actionBuy})`,
              borderRadius: 6,
              transition: 'width 0.6s ease',
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4, fontSize: 11, color: SEMANTIC.textSecondary }}>
            <span>{t('manage_money.flow_portfolios_value_label')} <strong style={{ color: SEMANTIC.ctaPrimary }}>{fmt(totalBalance)}</strong></span>
            <span>{t('manage_money.flow_unallocated_label')} <strong style={{ color: SEMANTIC.actionBuy }}>{fmt(unallocated)}</strong></span>
            <span>{t('manage_money.flow_equity_label')} <strong style={{ color: SEMANTIC.textPrimary }}>{fmt(alpacaEquity)}</strong></span>
          </div>
        </div>
      )}

      {/* Righe portafoglio */}
      {portfolios.length > 0 && (
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {portfolios.map(p => {
            const liq = Number(p.total_balance || 0);
            const pct = totalBalance > 0 ? (liq / totalBalance) * 100 : 0;
            return (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: SEMANTIC.ctaPrimary, flexShrink: 0 }} />
                <div style={{ flex: 1, fontSize: 12, color: SEMANTIC.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {p.Nome_tipo_modello || `Portfolio ${p.id}`}
                </div>
                <div style={{ fontSize: 12, color: SEMANTIC.ctaPrimary, fontWeight: 600, minWidth: 70, textAlign: 'right' }}>
                  {fmt(liq)}
                </div>
                <div style={{ width: 60, backgroundColor: SEMANTIC.backgroundApp, borderRadius: 4, height: 5, border: `1px solid ${SEMANTIC.backgroundDisabled}` }}>
                  <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', backgroundColor: SEMANTIC.ctaPrimary, borderRadius: 4 }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ArrowRight({ color, label, dashed }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 4px', flexShrink: 0 }}>
      <div style={{ fontSize: 10, color: SEMANTIC.textSecondary, marginBottom: 4, whiteSpace: 'nowrap' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {dashed
          ? <div style={{ width: 24, height: 2, backgroundImage: `repeating-linear-gradient(90deg, ${color} 0, ${color} 4px, transparent 4px, transparent 8px)` }} />
          : <div style={{ width: 24, height: 2, backgroundColor: color }} />
        }
        <div style={{ width: 0, height: 0, borderTop: '5px solid transparent', borderBottom: '5px solid transparent', borderLeft: `7px solid ${color}` }} />
      </div>
    </div>
  );
}

// ======================================================
// COMPONENTE PRINCIPALE
// ======================================================
export default function ManageMoney() {
  const userId = localStorage.getItem('userId');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { modal, showModal } = useAppModal();

  const [portfolios, setPortfolios] = useState([]);
  const [tradingMode, setTradingMode] = useState('paper');
  const [selectedId, setSelectedId] = useState(null);
  const [amount, setAmount] = useState('');
  const [loadingPortfolios, setLoadingPortfolios] = useState(true);
  const [liveValues, setLiveValues] = useState({});

  const [alpacaAccount, setAlpacaAccount] = useState(null);
  const [alpacaLoading, setAlpacaLoading] = useState(true);
  const [alpacaError, setAlpacaError] = useState(false);   // true = generic, 'invalid_credentials' = 401

  const fetchLiveValues = useCallback(async (pfs) => {
    if (!pfs || pfs.length === 0) return;
    const results = await Promise.allSettled(
      pfs.map(p => API.get(`/portfolio/${p.id}/live-value`))
    );
    const lv = {};
    pfs.forEach((p, i) => {
      if (results[i].status === 'fulfilled') lv[p.id] = results[i].value.data;
    });
    setLiveValues(lv);
  }, []);

  const fetchPortfolios = useCallback(async () => {
    try {
      const [pfRes, userRes] = await Promise.all([
        API.get(`/portfolios/${userId}`),
        API.get(`/users/${userId}/profile`),
      ]);
      const pfs = pfRes.data.portfolios || [];
      const mode = userRes.data.trading_mode || 'paper';
      setTradingMode(mode);
      const filtered = pfs.filter(p => (p.trading_mode || 'paper') === mode);
      setPortfolios(filtered);
      if (filtered.length > 0) setSelectedId(id => id || filtered[0].id);
      fetchLiveValues(filtered);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingPortfolios(false);
    }
  }, [userId, fetchLiveValues]);

  const fetchAlpacaAccount = useCallback(async () => {
    setAlpacaLoading(true);
    setAlpacaError(false);
    try {
      const res = await API.get(`/alpaca/account?user_id=${userId}`);
      setAlpacaAccount(res.data);
    } catch (err) {
      const isInvalid = err.response?.status === 401 || err.response?.data?.error === 'invalid_credentials';
      setAlpacaError(isInvalid ? 'invalid_credentials' : true);
    } finally {
      setAlpacaLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) { navigate('/'); return; }
    fetchPortfolios();
    fetchAlpacaAccount();
  }, [userId, navigate, fetchPortfolios, fetchAlpacaAccount]);

  const handleAction = async (action_type) => {
    const qty = parseFloat(amount);
    if (!qty || qty <= 0) {
      showModal({ type: 'warning', title: t('manage_money.invalid_amount') });
      return;
    }
    if (action_type === 'withdraw' && selected) {
      const maxWithdrawable = Math.max(0, Math.min(
        Number(selected.liquidity || 0),
        Number(selected.total_balance || 0) - Number(selected.min_investment || 10000)
      ));
      if (qty > maxWithdrawable) {
        showModal({ type: 'warning', title: t('manage_money.exceed_max_withdrawable', { max: fmt(maxWithdrawable) }) });
        return;
      }
    }
    try {
      await API.post('/transactions/fund', {
        portfolio_id: selectedId,
        action_type,
        quantity: qty,
      });
      showModal({
        type: 'success',
        title: action_type === 'fund'
          ? t('manage_money.success_fund')
          : t('manage_money.success_withdraw'),
      });
      setAmount('');
      fetchPortfolios();
    } catch {
      showModal({ type: 'error', title: t('manage_money.error_operation') });
    }
  };

  const enrichedPortfolios = portfolios.map(p => {
    const live = liveValues[p.id];
    if (!live) return p;
    return { ...p, total_balance: live.total_balance, liquidity: live.liquidity, asset_value: live.asset_value };
  });

  const selected = enrichedPortfolios.find(p => p.id === selectedId);

  const [closePortfolioId, setClosePortfolioId] = useState(null);
  const [closing, setClosing] = useState(false);

  const handleClosePortfolio = () => {
    if (!closePortfolioId) return;
    const p = portfolios.find(x => x.id === closePortfolioId);
    showModal({
      type: 'confirm',
      title: t('manage_money.close_confirm_title', { name: p?.Nome_tipo_modello || closePortfolioId }),
      message: t('manage_money.close_confirm_body'),
      confirmLabel: t('manage_money.close_confirm_btn'),
      onConfirm: async () => {
        setClosing(true);
        try {
          const res = await API.post(`/portfolio/${closePortfolioId}/close`);
          const { submitted = [], failed = [], market_closed = false } = res.data;
          if (failed.length === 0) {
            if (market_closed) {
              showModal({ type: 'success', title: t('manage_money.close_queued', { count: submitted.length }) });
            } else {
              showModal({ type: 'success', title: t('manage_money.close_success', { count: submitted.length }) });
            }
          } else {
            if (market_closed) {
              showModal({ type: 'warning', title: t('manage_money.close_market_closed_partial', { ok: submitted.length, fail: failed.length }) });
            } else {
              showModal({ type: 'warning', title: t('manage_money.close_partial', { ok: submitted.length, fail: failed.length }) });
            }
          }
          fetchPortfolios();
        } catch {
          showModal({ type: 'error', title: t('manage_money.error_operation') });
        } finally {
          setClosing(false);
        }
      },
    });
  };

  if (loadingPortfolios && alpacaLoading) {
    return (
      <div style={{
        backgroundColor: SEMANTIC.backgroundApp, color: SEMANTIC.textPrimary,
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 20,
      }}>
        {t('manage_money.loading')}
      </div>
    );
  }

  return (
    <>
      {modal}
      <div style={{
        backgroundColor: SEMANTIC.backgroundApp,
        color: SEMANTIC.textPrimary,
        minHeight: '100vh',
        padding: '20px',
        paddingBottom: '40px',
      }}>
        <BackButton to="/portfolio" />

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, marginBottom: 4 }}>
          <h3 style={{ margin: 0 }}>{t('manage_money.title')}</h3>
          {(() => {
            const isReal = tradingMode === 'real';
            return (
              <span style={{
                fontSize: 11, fontWeight: 700, letterSpacing: 0.6,
                padding: '4px 10px', borderRadius: 20,
                backgroundColor: SEMANTIC.backgroundApp,
                color: isReal ? SEMANTIC.ctaPrimary : SEMANTIC.actionBuy,
                border: `1px solid ${isReal ? SEMANTIC.ctaPrimary : SEMANTIC.actionBuy}`,
              }}>
                {isReal ? t('manage_money.real_trading_badge') : t('manage_money.paper_trading_badge')}
              </span>
            );
          })()}
        </div>
        <p style={{ color: SEMANTIC.textSecondary, fontSize: 13, marginBottom: 24 }}>
          {t('manage_money.subtitle')}
        </p>

        {/* TILE 1 — CONTO ALPACA */}
        <AlpacaAccountTile
          account={alpacaAccount}
          loading={alpacaLoading}
          error={alpacaError}
          onGoToSettings={() => navigate('/settings')}
          tradingMode={tradingMode}
        />

        {/* TILE 2 — FLUSSO FONDI */}
        <MoneyFlowChart account={alpacaAccount} portfolios={enrichedPortfolios} tradingMode={tradingMode} />

        {/* TILE 3 — ALLOCA AI PORTAFOGLI */}
        <div style={{ ...tileStyle, border: `1px solid ${SEMANTIC.ctaPrimary}` }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="bi bi-briefcase" style={{ color: SEMANTIC.ctaPrimary }} />
            {t('manage_money.allocate_title_withdraw')}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
            {enrichedPortfolios.map(p => {
              const isSelected = p.id === selectedId;
              return (
                <div
                  key={p.id}
                  onClick={() => setSelectedId(p.id)}
                  style={{
                    backgroundColor: isSelected ? SEMANTIC.backgroundApp : SEMANTIC.backgroundApp,
                    border: isSelected ? `2px solid ${SEMANTIC.ctaPrimary}` : `1px solid ${SEMANTIC.backgroundDisabled}`,
                    borderRadius: 10,
                    padding: '12px 16px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: isSelected ? SEMANTIC.ctaPrimary : SEMANTIC.textPrimary }}>
                      {p.Nome_tipo_modello || `Portfolio ${p.id}`}
                    </div>
                    <div style={{ fontSize: 12, color: SEMANTIC.textSecondary, marginTop: 2 }}>
                      {t('manage_money.liquidity')} <strong style={{ color: SEMANTIC.actionBuy }}>{fmt(p.liquidity)}</strong>
                    </div>
                    <div style={{ fontSize: 12, color: SEMANTIC.textSecondary, marginTop: 2 }}>
                      {t('manage_money.max_withdrawable')}{' '}
                      <strong style={{ color: SEMANTIC.actionSell }}>
                        {fmt(Math.max(0, Math.min(
                          Number(p.liquidity || 0),
                          Number(p.total_balance || 0) - Number(p.min_investment || 10000)
                        )))}
                      </strong>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>{fmt(p.total_balance)}</div>
                    {isSelected && <i className="bi bi-check-circle-fill" style={{ color: SEMANTIC.ctaPrimary, fontSize: 14 }} />}
                  </div>
                </div>
              );
            })}
          </div>

          {selected && (
            selected.status === 'closing' ? (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                backgroundColor: 'rgba(255,170,0,0.08)',
                border: '1px solid rgba(255,170,0,0.35)',
                borderRadius: 10, padding: '12px 14px',
              }}>
                <i className="bi bi-hourglass-split" style={{ color: '#ffaa00', fontSize: 16, marginTop: 1, flexShrink: 0 }} />
                <span style={{ color: '#ffaa00', fontSize: 13, lineHeight: 1.5 }}>
                  {t('manage_money.closing_funds_blocked')}
                </span>
              </div>
            ) : (
              <>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder={t('manage_money.amount_placeholder')}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: 8,
                    border: `1px solid ${SEMANTIC.backgroundDisabled}`,
                    backgroundColor: SEMANTIC.backgroundApp,
                    color: SEMANTIC.textPrimary,
                    fontSize: 16,
                    marginBottom: 14,
                    boxSizing: 'border-box',
                    outline: 'none',
                  }}
                />
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={() => handleAction('fund')}
                    style={{
                      flex: 1, padding: '13px 0', borderRadius: 50, border: 'none',
                      backgroundColor: SEMANTIC.ctaPrimary, color: SEMANTIC.ctaPrimaryText,
                      fontWeight: 700, fontSize: 14, cursor: 'pointer',
                    }}
                  >
                    <i className="bi bi-plus-circle" style={{ marginRight: 6 }} />
                    {t('manage_money.add_button')}
                  </button>
                  <button
                    onClick={() => handleAction('withdraw')}
                    style={{
                      flex: 1, padding: '13px 0', borderRadius: 50,
                      border: `2px solid ${SEMANTIC.actionSell}`,
                      backgroundColor: 'transparent', color: SEMANTIC.actionSell,
                      fontWeight: 700, fontSize: 14, cursor: 'pointer',
                    }}
                  >
                    <i className="bi bi-dash-circle" style={{ marginRight: 6 }} />
                    {t('manage_money.withdraw_button')}
                  </button>
                </div>
              </>
            )
          )}

          {portfolios.length === 0 && (
            <p style={{ color: SEMANTIC.textSecondary, textAlign: 'center', margin: 0 }}>{t('manage_money.no_portfolios')}</p>
          )}
        </div>

        {/* TILE 4 — CHIUDI PORTAFOGLIO */}
        {portfolios.length > 0 && (
          <div style={{ ...tileStyle, border: `1px solid ${SEMANTIC.actionSell}` }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="bi bi-x-circle" style={{ color: SEMANTIC.actionSell }} />
              {t('manage_money.close_title')}
            </div>
            <p style={{ color: SEMANTIC.textSecondary, fontSize: 13, margin: '0 0 16px', lineHeight: 1.5 }}>
              {t('manage_money.close_desc')}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {enrichedPortfolios.map(p => {
                const isSelected = p.id === closePortfolioId;
                const isClosing = p.status === 'closing';
                const hasAssets = Number(p.asset_value || 0) > 0;
                const hasPending = Number(p.pending_orders_count || 0) > 0;
                const selectable = !isClosing && p.status !== 'closed';
                return (
                  <div
                    key={p.id}
                    onClick={() => selectable && setClosePortfolioId(p.id)}
                    style={{
                      border: isSelected ? `2px solid ${SEMANTIC.actionSell}` : `1px solid ${SEMANTIC.backgroundDisabled}`,
                      borderRadius: 10,
                      padding: '10px 14px',
                      cursor: selectable ? 'pointer' : 'not-allowed',
                      opacity: selectable ? 1 : 0.45,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: SEMANTIC.backgroundApp,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: isSelected ? SEMANTIC.actionSell : SEMANTIC.textPrimary, display: 'flex', alignItems: 'center', gap: 8 }}>
                        {p.Nome_tipo_modello || `Portfolio ${p.id}`}
                        {p.status === 'closing' && (
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, backgroundColor: 'rgba(255,170,0,0.15)', color: '#ffaa00', border: '1px solid rgba(255,170,0,0.4)' }}>
                            {t('manage_money.status_closing')}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: SEMANTIC.textSecondary, marginTop: 2 }}>
                        {p.status === 'closing'
                          ? t('manage_money.closing_desc')
                          : hasAssets
                            ? <>{t('manage_money.assets_label')} <strong style={{ color: SEMANTIC.textPrimary }}>{fmt(p.asset_value)}</strong></>
                            : hasPending
                              ? t('manage_money.pending_orders_will_cancel')
                              : t('manage_money.no_assets')}
                      </div>
                    </div>
                    {isSelected && <i className="bi bi-check-circle-fill" style={{ color: SEMANTIC.actionSell, fontSize: 14 }} />}
                  </div>
                );
              })}
            </div>

            <button
              onClick={handleClosePortfolio}
              disabled={!closePortfolioId || closing}
              style={{
                width: '100%', padding: '13px 0', borderRadius: 50,
                border: `2px solid ${SEMANTIC.actionSell}`,
                backgroundColor: closePortfolioId && !closing ? `${SEMANTIC.actionSell}18` : 'transparent',
                color: SEMANTIC.actionSell,
                fontWeight: 700, fontSize: 14,
                cursor: closePortfolioId && !closing ? 'pointer' : 'not-allowed',
                opacity: closePortfolioId && !closing ? 1 : 0.5,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              {closing
                ? <><i className="bi bi-arrow-repeat" style={{ animation: 'spin 1s linear infinite' }} /> {t('manage_money.close_in_progress')}</>
                : <><i className="bi bi-x-circle" /> {t('manage_money.close_btn')}</>}
            </button>
          </div>
        )}

      </div>
    </>
  );
}

// ======================================================
// STILI CONDIVISI
// ======================================================
const tileStyle = {
  backgroundColor: SEMANTIC.backgroundCard,
  border: `1px solid ${SEMANTIC.backgroundDisabled}`,
  borderRadius: 14,
  padding: '20px',
  marginBottom: 16,
  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
};

const flowNodeStyle = (borderColor, bg) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: bg,
  border: `2px solid ${borderColor}`,
  borderRadius: 12,
  padding: '12px 14px',
  minWidth: 80,
  flexShrink: 0,
  color: SEMANTIC.textPrimary,
});

const badgeStyle = (color, bg) => ({
  fontSize: 11,
  fontWeight: 700,
  padding: '3px 10px',
  borderRadius: 20,
  backgroundColor: bg,
  color,
  border: `1px solid ${color}40`,
  letterSpacing: 0.5,
});
